import {Injectable} from '@angular/core';
import {
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationExtras,
  NavigationStart,
  Router,
  RouterEvent
} from '@angular/router';
import {BehaviorSubject} from 'rxjs';
import {SpinnerService} from '../spinner/spinner.service';
import {ModeDataService} from '../../editor-state/mode-data.service';
import {MapTreeService} from '../services/map-tree.service';
import {TrainingsService} from '../services/trainings.service';
import {get} from 'lodash';
import {ClientEnvService} from '../services/client-env.service';
import {NavHistoryObject, NavigationHistoryService} from '../services/navigation-history.service';
import {DIAGRAM_VERSION_STATES_ENUM} from '../../editor-interface/shared/models/diagram-version.constant';
import {EditorRegistryService} from 'src/app/diagram-render/mx-graph/editor/editor-registry.service';
import {DiagramTypeConfigService} from '../services/diagram-type-config.service';
import {MAPS_TYPES_ENUM} from '../../editor-interface/shared/models/map.model';
import {SessionStorageService} from '../services/session-storage.service';
import {DiagramSubtypeUrlMap} from '@q9elements/ui-kit/common';

export interface PossibleNavigationsData {
  diagramId?: string;
  state?: string;
}

export class NavObject {
  public id: string;
  public state: string;
  public top: string;
  public originSrc: string;
  public authorisation?: string;
  public subtype?: MAPS_TYPES_ENUM;

  constructor(
    id: string,
    state?: string,
    top?: string,
    originSrc?: string,
    authorisation?: string,
    subtype?: MAPS_TYPES_ENUM
  ) {
    this.id = id;
    this.state = state || DIAGRAM_VERSION_STATES_ENUM.DRAFT;
    this.top = top;
    this.originSrc = originSrc;
    this.authorisation = authorisation;
    this.subtype = subtype;
  }

  public isSame(nav: NavObject): boolean {
    if (!nav) {
      return false;
    }

    return this.id === nav.id && this.state === nav.state;
  }
}

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  public requestPossibleNavSubject: BehaviorSubject<PossibleNavigationsData> = new BehaviorSubject(
    {}
  );
  private currNav: NavObject;
  private navigationActive: boolean;

  constructor(
    private router: Router,
    private spinnerSvc: SpinnerService,
    private mapTreeSvc: MapTreeService,
    private modeDataSvc: ModeDataService,
    private trainingSvc: TrainingsService,
    private clientEnvService: ClientEnvService,
    private navigationHistoryService: NavigationHistoryService,
    private editorRegistryService: EditorRegistryService,
    private diagramTypeConfigService: DiagramTypeConfigService
  ) {
    router.events.subscribe((event: RouterEvent | Event) => this.navigationInterceptor(event));
  }

  public previous(): Promise<boolean | Error> {
    const prevNav = this.navigationHistoryService.lastNavItem();

    if (prevNav) {
      const mode = this.modeDataSvc.isEditMode();
      const {id, state} = prevNav;

      this.navigationHistoryService.navLastItem();

      return this.navigate(id, state, mode);
    } else {
      return Promise.resolve(false);
    }
  }

  public selected(index) {
    const selectedNav = this.navigationHistoryService.navSelectedItem(index);
    const id = selectedNav.id;
    const state = selectedNav.state;
    const mode = this.modeDataSvc.isEditMode();

    return this.navigate(id, state, mode);
  }

  /**
   * Navigates relative to current diagram state
   */
  public relative(
    id: string,
    argMode?: boolean,
    subtype?: MAPS_TYPES_ENUM
  ): Promise<boolean | Error> {
    const state = this.currNav.state;
    const mode = argMode === undefined ? this.modeDataSvc.isEditMode() : argMode;

    return this.navigate(id, state, mode, subtype);
  }

  public navigateToTopDiagram(topDiagramId: string) {
    const customTopDiagram = this.getCustomTop() || topDiagramId;

    return this.relative(customTopDiagram);
  }

  public current(mode: boolean, subtype?: MAPS_TYPES_ENUM): Promise<boolean | Error> {
    const id = this.currNav.id;

    return this.relative(id, mode, subtype);
  }

  public navigate(
    id: string,
    state?: string,
    mode: boolean = false,
    subtype?: MAPS_TYPES_ENUM
  ): Promise<boolean | Error> {
    if (this.navigationActive) {
      return Promise.resolve(false);
    }

    this.navigationActive = true;
    const originSrc = (this.currNav && this.currNav.originSrc) || null;
    const authorisation = (this.currNav && this.currNav.authorisation) || null;
    const top = (this.currNav && this.currNav.top) || null;
    const dvStorageKey = `${id}_${state}`;
    const dvMetaObject = SessionStorageService.get(dvStorageKey);
    const pendingSubtype = subtype || dvMetaObject?.subtype;
    const pendingNav = new NavObject(id, state, top, originSrc, authorisation, pendingSubtype);

    return this.toDiagram(pendingNav, mode)
      .then((navigationSuccess: boolean) => {
        this.navigationActive = false;

        if (navigationSuccess) {
          this.setCurrent(
            pendingNav.id,
            pendingNav.state,
            false,
            pendingNav.top,
            pendingNav.originSrc,
            pendingNav.authorisation
          );
          this.mapTreeSvc.triggerUpdate();
        }

        return navigationSuccess;
      })
      .catch(err => {
        this.navigationActive = false;
        throw err;
      });
  }

  public navigateWithoutQuery(id, state = '') {
    if (this.navigationActive) {
      return Promise.resolve(false);
    }

    this.navigationActive = true;
    const pendingNav = new NavObject(id, state, this.currNav.top);

    return this.toDiagram(pendingNav, false)
      .then((navigationSuccess: boolean) => {
        this.navigationActive = false;

        if (navigationSuccess) {
          this.setCurrent(
            pendingNav.id,
            pendingNav.state,
            false,
            pendingNav.top,
            pendingNav.originSrc,
            pendingNav.authorisation
          );
          this.mapTreeSvc.triggerUpdate();
        }

        return navigationSuccess;
      })
      .catch(err => {
        this.navigationActive = false;
        throw err;
      });
  }

  public navigateByObj(navObj: NavObject): Promise<boolean> {
    if (this.navigationActive) {
      return Promise.resolve(false);
    }

    this.navigationActive = true;

    return this.toDiagram(navObj, false)
      .then((navigationSuccess: boolean) => {
        this.navigationActive = false;

        if (navigationSuccess) {
          const {id, state, top, originSrc, authorisation} = navObj;

          this.setCurrent(id, state, false, top, originSrc, authorisation);
          this.mapTreeSvc.triggerUpdate();
        }

        return navigationSuccess;
      })
      .catch(err => {
        this.navigationActive = false;
        throw err;
      });
  }

  private navigationInterceptor(event: RouterEvent | Event): void {
    if (event instanceof NavigationStart) {
      this.navigationActive = true; // track all navigations, not just triggered by service
      this.spinnerSvc.show();
      this.editorRegistryService.getGraph()?.contextBar?.destroy();
    }

    if (event instanceof NavigationEnd) {
      this.navigationActive = false;
      // this.spinnerSvc.hide();
    }

    if (event instanceof NavigationCancel) {
      this.navigationActive = false;
      this.spinnerSvc.hide();
    }

    if (event instanceof NavigationError) {
      this.navigationActive = false;
      this.spinnerSvc.hide();

      const {error} = event;

      if (
        error?.error === 'locked' ||
        (error?.error?.status && error?.error?.status === 'diagram_rights.cannot_edit')
      ) {
        this.current(false);
      }
    }
  }

  private toDiagram(navObj: NavObject, mode: boolean): Promise<boolean | Error> {
    const {state, id, top, originSrc, authorisation, subtype} = navObj;
    const isStripped = this.clientEnvService.isStripped;
    const diagramSubtype = DiagramSubtypeUrlMap[subtype];

    const extras = {
      queryParams: {
        ...(diagramSubtype && {type: diagramSubtype}),
        v: state,
        top,
        originSrc,
        authorisation
      }
    } as NavigationExtras;

    if (isStripped) {
      extras.queryParams.stripped = true;
    }

    if (mode) {
      extras.queryParams.edit = true;
    }

    if (this.trainingSvc.isTrainingMode()) {
      extras.queryParams.training = this.trainingSvc.getCurrentId();
    }

    return this.router.navigate(['diagram', id], extras);
  }

  public setCurrent(
    id: string,
    state?: string,
    force?: boolean,
    top?: string,
    originSrc?: string,
    authorisation?: string
  ): void {
    if (!this.navigationActive || force) {
      if (this.currNav === undefined || !this.currNav.isSame({id, state} as NavObject)) {
        this.currNav = new NavObject(id, state, top, originSrc, authorisation);
      }
    }
  }

  public getCurrent(): NavObject {
    return this.currNav;
  }

  public getCustomTop(): string {
    return get(this.currNav, 'top');
  }

  public getPrevious(): NavHistoryObject {
    return this.navigationHistoryService.lastNavItem();
  }

  public isNavActive(): boolean {
    return Boolean(this.navigationActive);
  }

  public navigateByUrl(link) {
    const internalUrl = new RegExp(window.location.hostname);
    const absolute = new RegExp(/^\s*(https?|ftp|mailto|iwl):/, `im`);

    if (internalUrl.test(link)) {
      const diagramId = (link.match(/\b[0-9a-f]{24}\b/) || []).pop();
      let version = (link.match(/\b(\?|&)v=(r_[0-9a-f]{24}|[a-z]+)\b/) || []).pop();

      if (diagramId) {
        if (!version) {
          version = DIAGRAM_VERSION_STATES_ENUM.DRAFT;
        }

        return this.navigate(diagramId, version, false);
      }
    } else {
      return window.open(absolute.test(link) ? link : 'http://' + link, '_blank');
    }
  }
}
